import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, CheckCircle, Clock, XCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ContractManagement = () => {
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const signedAt = publisher?.contract_signed_at || publisher?.contract_signed_date;

  const getStatusInfo = () => {
    if (!publisher) {
      return {
        icon: Clock,
        iconColor: 'text-yellow-500',
        badge: 'Not Started',
        badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        message: 'Complete your publisher setup to sign the agreement.',
        action: 'Start Setup',
      };
    }

    if (!publisher.contract_signed) {
      return {
        icon: Clock,
        iconColor: 'text-yellow-500',
        badge: 'Pending Signature',
        badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        message: 'Review and sign the publisher agreement to activate your account.',
        action: 'Sign Agreement',
      };
    }

    // Signed — check account status
    const status = publisher.status;

    if (status === 'rejected') {
      return {
        icon: XCircle,
        iconColor: 'text-red-500',
        badge: 'Rejected',
        badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        message: publisher.rejection_reason || 'Your application was not approved.',
        action: null,
      };
    }

    if (status === 'suspended') {
      return {
        icon: XCircle,
        iconColor: 'text-orange-500',
        badge: 'Suspended',
        badgeClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
        message: 'Your publisher account has been suspended. Contact publishers@slicetv.app.',
        action: null,
      };
    }

    return {
      icon: CheckCircle,
      iconColor: 'text-green-500',
      badge: 'Signed',
      badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      message: signedAt
        ? `Signed on ${new Date(signedAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}`
        : 'Agreement accepted.',
      action: null,
    };
  };

  const info = getStatusInfo();
  const StatusIcon = info.icon;

  return (
    <div className='space-y-6'>
      {/* Main status card */}
      <Card>
        <CardHeader>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <StatusIcon className={`h-8 w-8 shrink-0 ${info.iconColor}`} />
              <div>
                <CardTitle className='text-base'>Publisher Agreement</CardTitle>
                <CardDescription className='text-sm mt-0.5'>{info.message}</CardDescription>
              </div>
            </div>
            <Badge className={`${info.badgeClass} shrink-0`}>{info.badge}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className='flex flex-wrap gap-3'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => navigate('/onboarding?step=contract')}
            >
              <ExternalLink className='h-4 w-4 mr-2' />
              View Agreement
            </Button>
            {info.action && (
              <Button
                size='sm'
                onClick={() => navigate('/onboarding?step=contract')}
              >
                {info.action}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contract details */}
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Agreement Details</CardTitle>
          <CardDescription>Kinora Publisher Agreement</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-start gap-4 p-4 rounded-lg border'>
            <div>
              {publisher?.contract_signed ? (
                <CheckCircle className='h-5 w-5 text-green-500 mt-0.5' />
              ) : (
                <FileText className='h-5 w-5 text-muted-foreground mt-0.5' />
              )}
            </div>
            <div className='flex-1'>
              <div className='flex flex-wrap items-center gap-2 mb-1'>
                <p className='font-medium text-sm'>Publisher Agreement</p>
                <Badge variant='outline' className='text-xs'>Required</Badge>
                {publisher?.contract_version && (
                  <Badge variant='secondary' className='text-xs'>
                    {publisher.contract_version}
                  </Badge>
                )}
              </div>
              <p className='text-xs text-muted-foreground'>
                Covers: slice mechanics, content rights, upload fee policy, revenue terms, moderation process, and platform rules.
              </p>
              {publisher?.contract_signed && signedAt && (
                <p className='text-xs text-muted-foreground mt-1'>
                  Accepted on {new Date(signedAt).toLocaleDateString('en-CA', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              )}
            </div>
            {!publisher?.contract_signed && (
              <Button
                size='sm'
                onClick={() => navigate('/onboarding?step=contract')}
              >
                Sign Now
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractManagement;
